import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  Dialog,
  Dropdown,
  Stack,
  Textarea,
  useToast,
} from 'braid-design-system';
import { useState } from 'react';

import translations from './.vocab';

const COMMENTS_CHARACTER_LIMIT = 400;

type ReportProfileFormProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string, comments: string) => Promise<boolean>;
};

type ReportProfileError = 'reason' | 'comments' | 'comments-limit';

export const ReportProfileForm = ({
  open,
  onClose,
  onSubmit,
}: ReportProfileFormProps) => {
  // TODO: Add translations for ID and TH
  const { t } = useTranslations(translations);
  const showToast = useToast();

  const [reason, setReason] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [errors, setErrors] = useState<ReportProfileError[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const onReasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setReason(event.target.value);
    removeErrors(['reason']);
  };

  const onCommentsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComments(event.target.value);
    removeErrors(['comments', 'comments-limit']);
  };

  const hasErrors = (types: ReportProfileError[]) =>
    errors.some((error) => types.includes(error));

  const removeErrors = (types: ReportProfileError[]) => {
    if (hasErrors(types)) {
      setErrors(errors.filter((error) => !types.includes(error)));
    }
  };

  const resetForm = () => {
    setReason('');
    setComments('');
    setErrors([]);
  };

  const resetFormAndClose = () => {
    resetForm();
    onClose();
  };

  const getTone = (hasError: boolean) => (hasError ? 'critical' : 'neutral');

  const getCommentsMessage = (types: ReportProfileError[]) => {
    const hasError = hasErrors(types);

    if (hasError && errors.includes('comments')) {
      return t('TEXTAREA_ERROR_MESSAGE');
    }

    if (hasError && errors.includes('comments-limit')) {
      return t('TEXTAREA_LIMIT_ERROR_MESSAGE', {
        count: COMMENTS_CHARACTER_LIMIT.toString(),
      });
    }

    return undefined;
  };

  const onClick = async () => {
    const sanitizedComments = comments.trim();
    const fieldErrors: ReportProfileError[] = [];

    if (!reason) {
      fieldErrors.push('reason');
    }

    if (!sanitizedComments) {
      fieldErrors.push('comments');
    }

    if (sanitizedComments.length > COMMENTS_CHARACTER_LIMIT) {
      fieldErrors.push('comments-limit');
    }

    if (fieldErrors.length > 0) {
      setErrors(fieldErrors);
      return;
    }

    try {
      setIsProcessing(true);

      const result = await onSubmit(reason, sanitizedComments);

      if (!result) {
        throw new Error('Failed to submit profile report');
      }

      showToast({
        tone: 'positive',
        message: t('SUBMIT_REPORT_SUCCESSFUL'),
      });

      resetFormAndClose();
    } catch {
      showToast({
        tone: 'critical',
        message: t('SUBMIT_REPORT_FAILED'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog onClose={resetFormAndClose} title={t('DIALOG_TITLE')} open={open}>
      <Stack space="medium">
        <Dropdown
          label={t('DROPDOWN_LABEL')}
          onChange={onReasonChange}
          value={reason}
          tone={getTone(hasErrors(['reason']))}
          message={
            hasErrors(['reason']) ? t('DROPDOWN_ERROR_MESSAGE') : undefined
          }
          placeholder={t('DROPDOWN_PLACEHOLDER')}
        >
          <option value="FRAUD_OR_SCAM">{t('REASON_FRAUD_OR_SCAM')}</option>
          <option value="INAPPROPRIATE_CONTENT">
            {t('REASON_INAPPROPRIATE_CONTENT')}
          </option>
          <option value="SELLING_BUSINESS_SERVICES">
            {t('REASON_SELLING_BUSINESS_SERVICES')}
          </option>
          <option value="FAKE_PROFILE">{t('REASON_FAKE_PROFILE')}</option>
        </Dropdown>

        <Textarea
          label={t('TEXTAREA_LABEL')}
          onChange={onCommentsChange}
          value={comments}
          tone={getTone(hasErrors(['comments', 'comments-limit']))}
          message={getCommentsMessage(['comments', 'comments-limit'])}
          placeholder={t('TEXTAREA_PLACEHOLDER')}
          characterLimit={COMMENTS_CHARACTER_LIMIT}
          reserveMessageSpace
        />

        <Actions>
          <Button
            variant="solid"
            tone="formAccent"
            onClick={onClick}
            loading={isProcessing}
          >
            {t('BUTTON_LABEL')}
          </Button>
        </Actions>
      </Stack>
    </Dialog>
  );
};
